import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { THEME_PRESETS, FONT_PRESETS, applyThemeVars } from "../../context/ContentContext";
import { ImageField } from "./ContentManager";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Check, Palette, Brush, Type } from "lucide-react";

export const ThemeSettings = () => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setForm(r.data || {})).catch(() => {});
    Object.values(FONT_PRESETS).forEach((f) => {
      if (f.gf && !document.querySelector(`link[href="${f.gf}"]`)) {
        const l = document.createElement("link");
        l.rel = "stylesheet";
        l.href = f.gf;
        document.head.appendChild(l);
      }
    });
  }, []);

  const preset = form.theme_preset || "orange";

  const update = (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    applyThemeVars(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", {
        values: {
          theme_preset: form.theme_preset || "orange",
          theme_custom_primary: form.theme_custom_primary || "",
          theme_custom_accent: form.theme_custom_accent || "",
          theme_bg_image: form.theme_bg_image || "",
          font_preset: form.font_preset || "modern",
        },
      });
      toast.success("Tema disimpan — langsung berlaku di seluruh situs");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="theme-settings-panel">
      <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
        <Palette size={15} className="text-orange-600" />
        Pilih tema warna situs. Pratinjau langsung diterapkan — klik Simpan untuk memberlakukannya bagi semua pengunjung.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {Object.entries(THEME_PRESETS).map(([key, t]) => (
          <button
            key={key}
            onClick={() => update({ theme_preset: key })}
            data-testid={`theme-${key}`}
            className={`hover-lift relative bg-white border-2 rounded-xl p-5 text-left transition-colors ${
              preset === key ? "border-slate-900" : "border-slate-200 hover:border-slate-400"
            }`}
          >
            {preset === key && (
              <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center">
                <Check size={13} />
              </span>
            )}
            <div className="flex gap-2 mb-3">
              <span className="w-8 h-8 rounded-full border border-black/10" style={{ background: t.primary }} />
              <span className="w-8 h-8 rounded-full border border-black/10" style={{ background: t.accent }} />
            </div>
            <p className="font-heading font-bold text-slate-900 text-sm">{t.name}</p>
          </button>
        ))}

        <button
          onClick={() => update({ theme_preset: "custom" })}
          data-testid="theme-custom"
          className={`hover-lift relative bg-white border-2 rounded-xl p-5 text-left transition-colors ${
            preset === "custom" ? "border-slate-900" : "border-slate-200 hover:border-slate-400"
          }`}
        >
          {preset === "custom" && (
            <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Check size={13} />
            </span>
          )}
          <div className="w-8 h-8 rounded-full mb-3 bg-[conic-gradient(red,orange,yellow,green,cyan,blue,violet,red)] border border-black/10 flex items-center justify-center">
            <Brush size={14} className="text-white drop-shadow" />
          </div>
          <p className="font-heading font-bold text-slate-900 text-sm">Kustom</p>
          <p className="text-xs text-slate-400 mt-0.5">Warna & background sendiri</p>
        </button>
      </div>

      {preset === "custom" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 mb-8" data-testid="theme-custom-panel">
          <h3 className="font-heading font-bold text-slate-900">Pengaturan Tema Kustom</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label>Warna Utama</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.theme_custom_primary || "#EA580C"}
                  onChange={(e) => update({ theme_custom_primary: e.target.value })}
                  className="w-14 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white"
                  data-testid="theme-custom-primary"
                />
                <span className="font-mono text-sm text-slate-500">{form.theme_custom_primary || "#EA580C"}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Warna Aksen</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.theme_custom_accent || "#0284C7"}
                  onChange={(e) => update({ theme_custom_accent: e.target.value })}
                  className="w-14 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white"
                  data-testid="theme-custom-accent"
                />
                <span className="font-mono text-sm text-slate-500">{form.theme_custom_accent || "#0284C7"}</span>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Background Gambar Situs (opsional)</Label>
            <ImageField value={form.theme_bg_image} onChange={(v) => update({ theme_bg_image: v })} testId="theme-bg-image" />
            <p className="text-xs text-slate-400">Gambar tampil samar sebagai latar seluruh halaman publik (diberi lapisan terang agar teks tetap terbaca).</p>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><Type size={16} className="text-orange-600" /> Jenis Huruf</h3>
        <p className="text-sm text-slate-500 mb-4">Pilih pasangan huruf untuk judul & teks isi di seluruh situs.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="font-presets">
          {Object.entries(FONT_PRESETS).map(([key, f]) => {
            const active = (form.font_preset || "modern") === key;
            return (
              <button
                key={key}
                onClick={() => update({ font_preset: key })}
                data-testid={`font-${key}`}
                className={`hover-lift relative bg-white border-2 rounded-xl p-5 text-left transition-colors ${
                  active ? "border-slate-900" : "border-slate-200 hover:border-slate-400"
                }`}
              >
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center">
                    <Check size={13} />
                  </span>
                )}
                <p className="text-2xl text-slate-900 leading-tight" style={{ fontFamily: f.heading }}>Aa Bb Cc</p>
                <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: f.body }}>Siap untuk selamat 123</p>
                <p className="font-heading font-bold text-slate-900 text-sm mt-3">{f.name}</p>
              </button>
            );
          })}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="btn-pop bg-orange-600 hover:bg-orange-500 rounded-full px-8 font-bold" data-testid="theme-save-btn">
        {saving ? "Menyimpan..." : "Simpan Tema"}
      </Button>
    </div>
  );
};
